import React, { useEffect, useMemo } from "react";

import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";

import MainLayout from "./components/MainLayout";
import { useUserInfo } from "./utils/security";

import {
    ChangePasswordPage,
    LoginPage,
    RequestPasswordChangePage,
    SignupPage,
} from "./components/Security";
import LandingPage from "./pages/landing";

import { Box, Hidden, Typography } from "@mui/material";
import { EmployeesIcon, EmployeesPage } from "./modules/employees";
import { FinancialsIcon, FinancialsPage } from "./modules/financials";
import { TimesheetsPage, TimesheetsIcon } from "./modules/timesheets";
import CounterpartiesPage, { CounterpartiesIcon } from "./pages/counterparties";
import FeaturesPage, { FeaturesIcon } from "./pages/features";
import ProjectsPage, { ProjectsIcon } from "./pages/projects";
import { SettingsPage } from "./modules/settings";
import { OnboardingPage } from "./modules/onboarding";
import { EstimatesIcon, PricelistsIcon } from "./modules/estimates";
import { EstimatesPage } from "./modules/estimates/EstimatesPage";
import { PricelistsPage } from "./modules/estimates/PricelistsPage";

const RedirectToRoot = ({ defaultRoute }) => {
    const navigate = useNavigate();

    useEffect(() => {
        navigate(defaultRoute, { replace: true });
    });
    return null;
};

const AppRoutes = () => {
    const { userInfo, hasRight } = useUserInfo();

    const routes = useMemo(() => {
        const routes = [];

        if (userInfo && !userInfo.initialized) {
            return [<Route key="onboarding" path="*" element={<OnboardingPage />} />];
        }

        if (userInfo) {
            let defaultRoute;

            userInfo.transactionsSupport &&
                hasRight("FINANCIALS_VIEW") &&
                routes.push({
                    path: "/financials/:id?",
                    title: "Finance",
                    link: "/financials",
                    icon: <FinancialsIcon />,
                    element: <FinancialsPage pagePath="financials" />,
                });
            userInfo.projectsSupport &&
                hasRight("PROJECTS_VIEW") &&
                routes.push({
                    path: "/projects/:id?",
                    title: "Projekty",
                    link: "/projects",
                    icon: <ProjectsIcon />,
                    element: <ProjectsPage />,
                });
            userInfo.counterpartiesSupport &&
                hasRight("COUNTERPARTY_VIEW") &&
                routes.push({
                    path: "/counterparties/:id?",
                    title: "Protistrany",
                    link: "/counterparties",
                    icon: <CounterpartiesIcon />,
                    element: <CounterpartiesPage />,
                });
            userInfo.pricelistsSupport &&
                hasRight("PRICELIST_VIEW") &&
                routes.push({
                    path: "/pricelists/:id?",
                    title: "Šablony nabídek",
                    link: "/pricelists",
                    icon: <PricelistsIcon />,
                    element: <PricelistsPage pagePath="pricelists" />,
                });
            userInfo.estimatesSupport &&
                hasRight("ESTIMATE_VIEW") &&
                routes.push({
                    path: "/estimates/:id?",
                    title: "Cenové nabídky",
                    link: "/estimates",
                    icon: <EstimatesIcon />,
                    element: <EstimatesPage pagePath="estimates" />,
                });
            userInfo.employeesSupport &&
                hasRight("EMPLOYEES_VIEW") &&
                routes.push({
                    path: "/employees/:id?",
                    title: "Lidé",
                    link: "/employees",
                    icon: <EmployeesIcon />,
                    element: <EmployeesPage pagePath="employees" />,
                });
            userInfo.timesheetsSupport &&
                hasRight("TIMESHEETS_VIEW") &&
                routes.push({
                    path: "/timesheets/:id?",
                    title: "Výkazy",
                    link: "/timesheets",
                    icon: <TimesheetsIcon />,
                    element: <TimesheetsPage />,
                });
            userInfo.hasFeatures &&
                !!userInfo.features &&
                hasRight("FEATURES_VIEW") &&
                routes.push({
                    path: "/features",
                    title: "Rozšíření",
                    icon: <FeaturesIcon />,
                    element: <FeaturesPage />,
                });

            if (routes.length) {
                defaultRoute = routes[0].link || routes[0].path;
            }

            return [
                <Route
                    key="main"
                    element={
                        <MainLayout
                            menuActions={routes.map((r) => ({
                                title: r.title,
                                icon: r.icon,
                                path: r.link || r.path,
                            }))}
                        />
                    }>
                    {routes.map((r) => (
                        <Route key={`key_${r.path}`} path={r.path} element={r.element} />
                    ))}
                    <Route path="/settings/:id?" element={<SettingsPage />} />
                    {!!defaultRoute && (
                        <Route path="*" element={<RedirectToRoot defaultRoute={defaultRoute} />} />
                    )}
                </Route>,
            ];
        }

        //public web pages

        //security pages
        routes.push({ path: "/login", element: <LoginPage /> });
        routes.push({ path: "/signup", element: <SignupPage /> });
        routes.push({ path: "/fp", element: <RequestPasswordChangePage /> });
        routes.push({ path: "/cp", element: <ChangePasswordPage /> });

        //main landing page
        routes.push({ path: "*", element: <LandingPage since={2009} /> });

        return routes.map((r) => <Route key={`key_${r.path}`} path={r.path} element={r.element} />);
    }, [userInfo, hasRight]);

    return <Routes>{routes}</Routes>;
};

const App = () => {
    return (
        <>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
            <Box sx={{ position: "fixed", bottom: 0, right: 0, backgroundColor: "#F6F6F6" }}>
                <Typography>
                    <Hidden smUp>xs</Hidden>
                    <Hidden smDown mdUp>
                        sm
                    </Hidden>
                    <Hidden mdDown lgUp>
                        md
                    </Hidden>
                    <Hidden lgDown xlUp>
                        lg
                    </Hidden>
                    <Hidden xlDown>xl</Hidden>
                </Typography>
            </Box>
        </>
    );
};

export default App;
